"use client";

import { type AudioInspection, inspectAudio } from "mediatools";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import type { EditorRecentSession, EditorSource, EditorSourceSessionSave } from "./adapters";
import {
  deleteSession,
  ensurePersistentStorage,
  fingerprint,
  listSessions,
  loadSession,
  type SavedSession,
  saveSession,
} from "./persistence";
import { loadSubtitleFiles } from "./subtitleFileLoader";

export interface LocalFileMediaReference {
  kind: "local-file";
  file: File;
  name: string;
  mimeType: string;
  size: number;
  lastModified: number;
}

export function isLocalFileMedia(media: unknown): media is LocalFileMediaReference {
  const candidate = media as Partial<LocalFileMediaReference> | null;
  return (
    candidate !== null &&
    typeof candidate === "object" &&
    candidate.kind === "local-file" &&
    typeof File !== "undefined" &&
    candidate.file instanceof File
  );
}

interface LoadLocalFileSourceInput {
  file: File;
  setFingerprint(fp: string): void;
  isCurrentFingerprint(fp: string): boolean;
  setPendingRestore(session: SavedSession | null): void;
  setMedia(media: LocalFileMediaReference): void;
  setPreviewUrl(url: string): void;
  inspect(file: File): Promise<AudioInspection>;
  loadSavedSession(fp: string): Promise<SavedSession | undefined>;
  createObjectUrl(file: File): string;
}

async function loadLocalFileSource({
  file,
  setFingerprint,
  isCurrentFingerprint,
  setPendingRestore,
  setMedia,
  setPreviewUrl,
  inspect,
  loadSavedSession,
  createObjectUrl,
}: LoadLocalFileSourceInput): Promise<void> {
  const fp = fingerprint(file);
  setFingerprint(fp);
  setPendingRestore(null);
  void inspect(file);
  // Load any saved session BEFORE starting the video. The metadata event consumes
  // the pending restore; if the video starts first it can replace and autosave an
  // empty doc over saved edits.
  const saved = await loadSavedSession(fp);
  if (!isCurrentFingerprint(fp)) return;
  setPendingRestore(saved ?? null);
  setMedia({
    kind: "local-file",
    file,
    name: file.name,
    mimeType: file.type,
    size: file.size,
    lastModified: file.lastModified,
  });
  setPreviewUrl(createObjectUrl(file));
}

function toEditorSession(session: SavedSession): EditorRecentSession {
  return session;
}

export function useLocalFileSource(): EditorSource {
  const t = useTranslations("mediaTools.editor.errors");
  const [media, setMedia] = useState<LocalFileMediaReference | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [recent, setRecent] = useState<EditorRecentSession[]>([]);
  const [restored, setRestored] = useState(false);
  const [undecodableAudio, setUndecodableAudio] = useState<string | null>(null);
  const [audioAvailable, setAudioAvailable] = useState(true);
  const fpRef = useRef<string | null>(null);
  const pendingRestore = useRef<SavedSession | null>(null);

  useEffect(() => {
    void listSessions().then((sessions) => setRecent(sessions.map(toEditorSession)));
  }, []);

  useEffect(() => {
    if (!previewUrl) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const open = useCallback(async (files: File[]) => {
    const next = files[0];
    if (!next) return;
    setRestored(false);
    setUndecodableAudio(null);
    setAudioAvailable(true); // optimistic until inspection resolves (avoids a flash)
    await loadLocalFileSource({
      file: next,
      setFingerprint: (fp) => {
        fpRef.current = fp;
      },
      isCurrentFingerprint: (fp) => fpRef.current === fp,
      setPendingRestore: (session) => {
        pendingRestore.current = session;
      },
      setMedia,
      setPreviewUrl,
      inspect: async (file) => {
        const info = await inspectAudio(file);
        if (fpRef.current !== fingerprint(file)) return info;
        if (info.hasAudio && !info.decodable) setUndecodableAudio(info.codec ?? "unknown");
        setAudioAvailable(info.hasAudio && info.decodable);
        return info;
      },
      loadSavedSession: loadSession,
      createObjectUrl: URL.createObjectURL,
    });
  }, []);

  const removeRecent = useCallback((fp: string) => {
    void deleteSession(fp);
    setRecent((list) => list.filter((session) => session.fp !== fp));
  }, []);

  const consumeLoadedDuration = useCallback(() => {
    const saved = pendingRestore.current;
    pendingRestore.current = null;
    setRestored(Boolean(saved));
    return saved ? toEditorSession(saved) : null;
  }, []);

  const persistSession = useCallback((input: EditorSourceSessionSave) => {
    if (!isLocalFileMedia(input.media)) return;
    const fp = fingerprint(input.media.file);
    void saveSession({
      fp,
      meta: {
        name: input.media.file.name,
        size: input.media.file.size,
        type: input.media.file.type,
        lastModified: input.media.file.lastModified,
        duration: input.duration,
      },
      doc: input.doc,
      originalCues: input.originalCues ?? undefined,
      savedAt: input.savedAt,
    });
  }, []);
  const persistStorage = useCallback(() => {
    void ensurePersistentStorage();
  }, []);
  const loadLocalizedSubtitleFiles = useCallback(
    (files: File[]) =>
      loadSubtitleFiles(files, {
        readError: t("subtitleRead"),
        emptyError: t("subtitleEmpty"),
      }),
    [t]
  );

  return {
    media,
    previewUrl,
    recent,
    restored,
    setRestored,
    audioAvailable,
    undecodableAudio,
    open,
    removeRecent,
    consumeLoadedDuration,
    loadSubtitleFiles: loadLocalizedSubtitleFiles,
    saveSession: persistSession,
    ensurePersistentStorage: persistStorage,
  };
}
